import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
const debug = process.env.NODE_ENV === 'development';
const STALL_NUDGE_SECONDS = 0.25;
const BUFFER_HIGH_WATERMARK_SECONDS = 10;
const TRACK_REMOVED_RECOVERY_COOLDOWN_MS = 750;
const TRACK_REMOVED_RECOVERY_MAX_ATTEMPTS = 3;
const HARD_RESET_COOLDOWN_MS = 2000;
const HARD_RESET_MAX_ATTEMPTS_PER_URL = 2;
const FRAGMENT_SKIP_ERROR_THRESHOLD = 3;
const FRAGMENT_SKIP_OFFSET_SECONDS = 0.05;
const TRACK_REMOVED_STORM_WINDOW_MS = 5000;
const TRACK_REMOVED_STORM_MAX_ERRORS = 8;
// Returns the number of seconds currently buffered ahead of the playhead.
const getBufferedAheadSeconds = (video) => {
    const { buffered, currentTime } = video;
    for (let i = 0; i < buffered.length; i += 1) {
        const start = buffered.start(i);
        const end = buffered.end(i);
        if (currentTime >= start && currentTime <= end) {
            return Math.max(0, end - currentTime);
        }
    }
    return 0;
};
export const HLS_Player = ({ url, ...eventFns }) => {
    const videoRef = useRef(null);
    const diagnosticsInitializedRef = useRef(false);
    const trackRemovedRecoveryRef = useRef({
        url: '',
        attempts: 0,
        lastAtMs: 0,
    });
    const hardResetRef = useRef({
        url: '',
        attempts: 0,
        lastAtMs: 0,
    });
    const repeatedFragErrorRef = useRef({ key: '', count: 0 });
    const trackRemovedStormRef = useRef({
        windowStartMs: 0,
        count: 0,
        halted: false,
    });
    const recentFragmentTimelineRef = useRef([]);
    const hlsSupported = Hls.isSupported();
    const [hlsSessionNonce, setHlsSessionNonce] = useState(0);
    const [hlsStatus, setHlsStatus] = useState('idle');
    const [playerEvent, setPlayerEvent] = useState('idle');
    const [lastHlsError, setLastHlsError] = useState('none');
    const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [bufferedAheadSeconds, setBufferedAheadSeconds] = useState(0);
    const [bufferedRangesSummary, setBufferedRangesSummary] = useState('none');
    const [videoReadyState, setVideoReadyState] = useState(0);
    const [videoNetworkState, setVideoNetworkState] = useState(0);
    const [paused, setPaused] = useState(true);
    const [errorCountByKey, setErrorCountByKey] = useState({});
    const [recentErrorSnapshots, setRecentErrorSnapshots] = useState([]);
    const [lastRecoveryAction, setLastRecoveryAction] = useState('none');
    const [bufferLifecycleEvents, setBufferLifecycleEvents] = useState([]);
    const [unplayableSegment, setUnplayableSegment] = useState({
        detected: false,
        reason: 'none',
        firstFragSn: 'none',
        lastFragSn: 'none',
        fromSec: 0,
        toSec: 0,
        beforeFragSn: 'none',
        beforeFragEndSec: 0,
        timelineSummary: 'none',
    });
    const unplayableIncidentReport = unplayableSegment.detected
        ? [
            'KVS_UNPLAYABLE_SEGMENT',
            `reason=${unplayableSegment.reason}`,
            `window_sn=${unplayableSegment.firstFragSn}-${unplayableSegment.lastFragSn}`,
            `window_sec=${unplayableSegment.fromSec.toFixed(3)}-${unplayableSegment.toSec.toFixed(3)}`,
            `before_sn=${unplayableSegment.beforeFragSn}`,
            `before_end_sec=${unplayableSegment.beforeFragEndSec.toFixed(3)}`,
            `last_error=${lastHlsError}`,
            `last_recovery=${lastRecoveryAction}`,
            `url=${url}`,
        ].join(' | ')
        : '';
    const getBufferedRangesSummary = (video) => {
        if (video.buffered.length === 0) {
            return 'none';
        }
        const ranges = [];
        for (let i = 0; i < video.buffered.length; i += 1) {
            const start = video.buffered.start(i);
            const end = video.buffered.end(i);
            ranges.push(`${start.toFixed(2)}-${end.toFixed(2)}`);
        }
        return ranges.join(', ');
    };
    const pushErrorSnapshot = (snapshot) => {
        setRecentErrorSnapshots((prev) => [snapshot, ...prev].slice(0, 8));
    };
    const pushLifecycleEvent = (event) => {
        const at = new Date().toISOString();
        setBufferLifecycleEvents((prev) => [`${at} ${event}`, ...prev].slice(0, 12));
    };
    const onCanPlay = (e) => {
        setPlayerEvent('canplay');
        setPaused(e.target.paused);
        eventFns.onCanPlay?.(e);
    };
    const onCanPlayThrough = (e) => {
        setPlayerEvent('canplaythrough');
        setPaused(e.target.paused);
        eventFns.onCanPlayThrough?.(e);
    };
    const onTimeUpdate = (e) => {
        setPlayerEvent('timeupdate');
        setPaused(e.target.paused);
        eventFns.onTimeUpdate?.(e);
    };
    const onError = (e) => {
        setPlayerEvent('error');
        const err = e.target.error;
        setLastHlsError(err ? `${err.code}:${err.message}` : 'video element error');
        eventFns.onError?.(e);
    };
    const onAbort = (e) => {
        setPlayerEvent('abort');
        eventFns.onAbort?.(e);
    };
    const onStalled = (e) => {
        setPlayerEvent('stalled');
        eventFns.onStalled?.(e);
    };
    const onSuspend = (e) => {
        setPlayerEvent('suspend');
        eventFns.onSuspend?.(e);
    };
    const onWaiting = (e) => {
        setPlayerEvent('waiting');
        eventFns.onWaiting?.(e);
    };
    const onEmptied = (e) => {
        setPlayerEvent('emptied');
        eventFns.onEmptied?.(e);
    };
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !url) {
            return;
        }
        trackRemovedRecoveryRef.current = { url, attempts: 0, lastAtMs: 0 };
        hardResetRef.current = { url, attempts: 0, lastAtMs: 0 };
        repeatedFragErrorRef.current = { key: '', count: 0 };
        trackRemovedStormRef.current = { windowStartMs: 0, count: 0, halted: false };
        recentFragmentTimelineRef.current = [];
        diagnosticsInitializedRef.current = false;
        setUnplayableSegment({
            detected: false,
            reason: 'none',
            firstFragSn: 'none',
            lastFragSn: 'none',
            fromSec: 0,
            toSec: 0,
            beforeFragSn: 'none',
            beforeFragEndSec: 0,
            timelineSummary: 'none',
        });
        if (!Hls.isSupported()) {
            video.src = url;
            return;
        }
        const hls = new Hls({
            // Keep standard latency behavior for archived playback rather than live tuning.
            lowLatencyMode: false,
            // Do not prefetch fragments before playback starts to reduce extra requests.
            startFragPrefetch: false,
            // Cap forward buffer size to avoid over-buffering and memory churn.
            maxBufferLength: BUFFER_HIGH_WATERMARK_SECONDS,
            // Keep the hard max aligned with maxBufferLength for predictable buffering.
            maxMaxBufferLength: BUFFER_HIGH_WATERMARK_SECONDS,
            // Retain a small amount of past media to support short seeks while limiting memory.
            backBufferLength: 15,
            // Allow a small discontinuity gap before treating playback as stalled.
            maxBufferHole: 0.5,
            // Nudge playhead by a small amount during stall recovery.
            nudgeOffset: 0.2,
            // Retry several nudges before giving up on automatic nudge recovery.
            nudgeMaxRetry: 8,
            // Check buffered health frequently so stalls are detected quickly.
            highBufferWatchdogPeriod: 1,
        });
        // Loads the HLS source once hls.js is attached to the video element.
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
            // Start each media session with a clean diagnostics slate so logs remain URL-specific.
            if (!diagnosticsInitializedRef.current) {
                diagnosticsInitializedRef.current = true;
                setErrorCountByKey({});
                setRecentErrorSnapshots([]);
                setBufferLifecycleEvents([]);
                setLastHlsError('none');
                setLastRecoveryAction('none');
            }
            setHlsStatus('media attached');
            pushLifecycleEvent('MEDIA_ATTACHED -> loadSource');
            hls.loadSource(url);
        });
        // Logs when hls.js disconnects from media, which can remove SourceBuffers.
        hls.on(Hls.Events.MEDIA_DETACHED, () => {
            pushLifecycleEvent('MEDIA_DETACHED');
        });
        // Logs when hls.js creates SourceBuffers for tracks.
        hls.on(Hls.Events.BUFFER_CREATED, (_, data) => {
            const tracks = data?.tracks ? Object.keys(data.tracks).join(',') : 'none';
            pushLifecycleEvent(`BUFFER_CREATED tracks=${tracks}`);
        });
        // Logs when hls.js resets buffer state and may recreate/remove track buffers.
        hls.on(Hls.Events.BUFFER_RESET, () => {
            pushLifecycleEvent('BUFFER_RESET');
        });
        // Starts playback after hls.js has parsed available stream levels.
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setHlsStatus('manifest parsed');
            void video.play().catch(() => { });
        });
        // Handles hls.js errors with stall nudging and fatal error recovery.
        hls.on(Hls.Events.ERROR, (_, data) => {
            const errorSummary = `${data.fatal ? 'fatal' : 'nonfatal'} ${data.type}:${data.details}`;
            setHlsStatus(`error ${errorSummary}`);
            setLastHlsError(errorSummary);
            const errorKey = `${data.type}:${data.details}`;
            setErrorCountByKey((prev) => ({ ...prev, [errorKey]: (prev[errorKey] || 0) + 1 }));
            const sourceError = (data.error ?? null);
            const frag = (data.frag ?? null);
            const part = (data.part ?? null);
            let recoveryAction = 'none';
            const fragSnValue = frag?.sn !== undefined ? String(frag.sn) : 'none';
            const fragStartValue = Number.isFinite(frag?.start) ? frag?.start : Number.NaN;
            const fragDurationValue = Number.isFinite(frag?.duration) ? frag?.duration : Number.NaN;
            if (Number.isFinite(fragStartValue) && Number.isFinite(fragDurationValue)) {
                const startSec = fragStartValue;
                const endSec = fragStartValue + fragDurationValue;
                const entryKey = `${fragSnValue}:${startSec.toFixed(3)}:${endSec.toFixed(3)}`;
                const hasEntry = recentFragmentTimelineRef.current.some((entry) => `${entry.sn}:${entry.startSec.toFixed(3)}:${entry.endSec.toFixed(3)}` === entryKey);
                if (!hasEntry) {
                    recentFragmentTimelineRef.current = [...recentFragmentTimelineRef.current, { sn: fragSnValue, startSec, endSec }].slice(-24);
                }
            }
            const isTrackRemovedBufferAppendError = data.type === Hls.ErrorTypes.MEDIA_ERROR &&
                data.details === Hls.ErrorDetails.BUFFER_APPEND_ERROR &&
                sourceError?.name === 'HlsJsTrackRemovedError';
            if (isTrackRemovedBufferAppendError) {
                const nowMs = Date.now();
                if (trackRemovedStormRef.current.windowStartMs === 0 ||
                    nowMs - trackRemovedStormRef.current.windowStartMs > TRACK_REMOVED_STORM_WINDOW_MS) {
                    trackRemovedStormRef.current.windowStartMs = nowMs;
                    trackRemovedStormRef.current.count = 1;
                }
                else {
                    trackRemovedStormRef.current.count += 1;
                }
                if (trackRemovedStormRef.current.count >= TRACK_REMOVED_STORM_MAX_ERRORS) {
                    trackRemovedStormRef.current.halted = true;
                    hls.stopLoad();
                    video.pause();
                    setHlsStatus('halted: persistent track-removed buffer append errors');
                    recoveryAction = `halted after ${trackRemovedStormRef.current.count} track-removed append errors in ${TRACK_REMOVED_STORM_WINDOW_MS}ms`;
                    pushLifecycleEvent(`TRACK_REMOVED_HALT count=${trackRemovedStormRef.current.count} windowMs=${TRACK_REMOVED_STORM_WINDOW_MS}`);
                    // Mark the current media selection as unplayable when sequential track-removed append errors form a burst.
                    setUnplayableSegment((prev) => {
                        const timeline = [...recentFragmentTimelineRef.current].sort((a, b) => a.startSec - b.startSec);
                        const beforeCandidate = Number.isFinite(fragStartValue)
                            ? [...timeline].reverse().find((entry) => entry.endSec <= fragStartValue)
                            : undefined;
                        const timelineSummary = timeline
                            .slice(-8)
                            .map((entry) => `sn ${entry.sn} ${entry.startSec.toFixed(3)}-${entry.endSec.toFixed(3)}s`)
                            .join(' | ');
                        const fromSec = Number.isFinite(fragStartValue)
                            ? (prev.detected ? Math.min(prev.fromSec, fragStartValue) : fragStartValue)
                            : prev.fromSec;
                        const toSec = Number.isFinite(fragStartValue) && Number.isFinite(fragDurationValue)
                            ? Math.max(prev.toSec, fragStartValue + fragDurationValue)
                            : prev.toSec;
                        return {
                            detected: true,
                            reason: `track-removed append storm (${trackRemovedStormRef.current.count} in ${TRACK_REMOVED_STORM_WINDOW_MS}ms)`,
                            firstFragSn: prev.detected && prev.firstFragSn !== 'none' ? prev.firstFragSn : fragSnValue,
                            lastFragSn: fragSnValue,
                            fromSec,
                            toSec,
                            beforeFragSn: beforeCandidate?.sn || prev.beforeFragSn || 'none',
                            beforeFragEndSec: beforeCandidate?.endSec ?? prev.beforeFragEndSec,
                            timelineSummary: timelineSummary || prev.timelineSummary || 'none',
                        };
                    });
                }
            }
            const repeatedFragKey = `${data.type}:${data.details}:${fragSnValue}`;
            if (repeatedFragErrorRef.current.key === repeatedFragKey) {
                repeatedFragErrorRef.current.count += 1;
            }
            else {
                repeatedFragErrorRef.current.key = repeatedFragKey;
                repeatedFragErrorRef.current.count = 1;
            }
            if (recoveryAction === 'none' &&
                isTrackRemovedBufferAppendError &&
                Number.isFinite(fragStartValue) &&
                Number.isFinite(fragDurationValue)) {
                const repeatOffset = Math.max(0, repeatedFragErrorRef.current.count - 1) * fragDurationValue;
                const skipToSeconds = fragStartValue + fragDurationValue + repeatOffset + FRAGMENT_SKIP_OFFSET_SECONDS;
                const nextTime = Math.max(video.currentTime, skipToSeconds);
                video.currentTime = nextTime;
                hls.startLoad(nextTime);
                void video.play().catch(() => { });
                recoveryAction = `track-removed skip sn=${fragSnValue} to t=${nextTime.toFixed(3)} repeat=${repeatedFragErrorRef.current.count}`;
                pushLifecycleEvent(`TRACK_REMOVED_SKIP sn=${fragSnValue} skipTo=${nextTime.toFixed(3)} repeat=${repeatedFragErrorRef.current.count}`);
            }
            if (!isTrackRemovedBufferAppendError &&
                data.type === Hls.ErrorTypes.MEDIA_ERROR &&
                data.details === Hls.ErrorDetails.BUFFER_APPEND_ERROR &&
                Number.isFinite(fragStartValue) &&
                Number.isFinite(fragDurationValue) &&
                repeatedFragErrorRef.current.count >= FRAGMENT_SKIP_ERROR_THRESHOLD) {
                const skipToSeconds = fragStartValue + fragDurationValue + FRAGMENT_SKIP_OFFSET_SECONDS;
                video.currentTime = skipToSeconds;
                hls.startLoad(skipToSeconds);
                recoveryAction = `skip failing fragment sn=${fragSnValue} to t=${skipToSeconds.toFixed(3)} after ${repeatedFragErrorRef.current.count} errors`;
                pushLifecycleEvent(`FRAGMENT_SKIP sn=${fragSnValue} skipTo=${skipToSeconds.toFixed(3)} count=${repeatedFragErrorRef.current.count}`);
            }
            if (recoveryAction === 'none' && isTrackRemovedBufferAppendError) {
                const nowMs = Date.now();
                const recoveryState = trackRemovedRecoveryRef.current;
                if (recoveryState.url !== url) {
                    recoveryState.url = url;
                    recoveryState.attempts = 0;
                    recoveryState.lastAtMs = 0;
                }
                const isCooldownElapsed = nowMs - recoveryState.lastAtMs >= TRACK_REMOVED_RECOVERY_COOLDOWN_MS;
                const hasAttemptsRemaining = recoveryState.attempts < TRACK_REMOVED_RECOVERY_MAX_ATTEMPTS;
                const allowAttemptNow = hasAttemptsRemaining && (Boolean(data.fatal) || isCooldownElapsed);
                if (allowAttemptNow) {
                    recoveryState.attempts += 1;
                    recoveryState.lastAtMs = nowMs;
                    // Rebuild media attachment when SourceBuffer has been removed unexpectedly.
                    hls.stopLoad();
                    hls.detachMedia();
                    hls.attachMedia(video);
                    hls.loadSource(url);
                    hls.startLoad(-1);
                    setHlsStatus('recovering track removed SourceBuffer');
                    recoveryAction = `track-removed reattach/load (attempt ${recoveryState.attempts}/${TRACK_REMOVED_RECOVERY_MAX_ATTEMPTS})`;
                    pushLifecycleEvent(`TRACK_REMOVED_RECOVERY attempt=${recoveryState.attempts} cooldownMs=${TRACK_REMOVED_RECOVERY_COOLDOWN_MS}`);
                }
                else if (data.fatal) {
                    const hardResetState = hardResetRef.current;
                    if (hardResetState.url !== url) {
                        hardResetState.url = url;
                        hardResetState.attempts = 0;
                        hardResetState.lastAtMs = 0;
                    }
                    const canHardResetNow = hardResetState.attempts < HARD_RESET_MAX_ATTEMPTS_PER_URL && nowMs - hardResetState.lastAtMs >= HARD_RESET_COOLDOWN_MS;
                    if (canHardResetNow) {
                        hardResetState.attempts += 1;
                        hardResetState.lastAtMs = nowMs;
                        recoveryAction = `track-removed hard reset HLS instance (${hardResetState.attempts}/${HARD_RESET_MAX_ATTEMPTS_PER_URL})`;
                        setHlsStatus('hard resetting hls instance');
                        pushLifecycleEvent(`TRACK_REMOVED_HARD_RESET attempt=${hardResetState.attempts} cooldownMs=${HARD_RESET_COOLDOWN_MS}`);
                        setHlsSessionNonce((prev) => prev + 1);
                    }
                    else {
                        // Keep fatal path active when hard reset is unavailable.
                        setHlsStatus('recovering track removed media error');
                        hls.recoverMediaError();
                        recoveryAction = `track-removed fallback recoverMediaError (hard reset unavailable ${hardResetState.attempts}/${HARD_RESET_MAX_ATTEMPTS_PER_URL})`;
                    }
                }
                else {
                    recoveryAction = hasAttemptsRemaining
                        ? `track-removed recovery throttled (cooldown ${TRACK_REMOVED_RECOVERY_COOLDOWN_MS}ms)`
                        : `track-removed recovery exhausted (${TRACK_REMOVED_RECOVERY_MAX_ATTEMPTS})`;
                }
            }
            if (recoveryAction === 'none' && !data.fatal && data.type === Hls.ErrorTypes.MEDIA_ERROR && data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                const bufferedAhead = getBufferedAheadSeconds(video);
                if (bufferedAhead > 1) {
                    const before = video.currentTime;
                    const durationLimit = Number.isFinite(video.duration) ? video.duration : before + STALL_NUDGE_SECONDS;
                    const after = Math.min(before + STALL_NUDGE_SECONDS, durationLimit);
                    if (after > before) {
                        video.currentTime = after;
                        void video.play().catch(() => { });
                    }
                }
                hls.startLoad();
                recoveryAction = 'startLoad (stall recovery)';
            }
            else if (recoveryAction === 'none' && data.fatal) {
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        setHlsStatus('recovering network error');
                        hls.startLoad();
                        recoveryAction = 'startLoad (fatal network)';
                        break;
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        setHlsStatus('recovering media error');
                        hls.recoverMediaError();
                        recoveryAction = 'recoverMediaError (fatal media)';
                        break;
                    default:
                        setHlsStatus('destroyed after unrecoverable error');
                        hls.destroy();
                        recoveryAction = 'destroy (unrecoverable)';
                        break;
                }
            }
            setLastRecoveryAction(recoveryAction);
            pushLifecycleEvent(`ERROR ${errorSummary} parent=${data.parent ? String(data.parent) : 'none'} srcErr=${sourceError?.name || 'none'} action=${recoveryAction}`);
            pushErrorSnapshot({
                at: new Date().toISOString(),
                fatal: Boolean(data.fatal),
                type: String(data.type),
                details: String(data.details),
                parent: data.parent ? String(data.parent) : 'none',
                reason: data.reason ? String(data.reason) : 'none',
                sourceErrorName: sourceError?.name || 'none',
                sourceErrorMessage: sourceError?.message || 'none',
                sourceErrorCode: sourceError?.code !== undefined ? String(sourceError.code) : 'none',
                fragSn: frag?.sn !== undefined ? String(frag.sn) : 'none',
                fragCc: frag?.cc !== undefined ? String(frag.cc) : 'none',
                fragStart: Number.isFinite(frag?.start) ? (frag?.start).toFixed(3) : 'none',
                fragDuration: Number.isFinite(frag?.duration) ? (frag?.duration).toFixed(3) : 'none',
                fragRelUrl: frag?.relurl || 'none',
                partIndex: part?.index !== undefined ? String(part.index) : 'none',
                level: frag?.level !== undefined ? String(frag.level) : 'none',
                recoveryAction,
                mediaAttached: Boolean(hls.media),
                event: playerEvent,
                currentTime: video.currentTime || 0,
                duration: Number.isFinite(video.duration) ? video.duration : 0,
                bufferedAhead: getBufferedAheadSeconds(video),
                bufferedRanges: getBufferedRangesSummary(video),
                readyState: video.readyState,
                networkState: video.networkState,
                paused: video.paused,
            });
        });
        hls.attachMedia(video);
        return () => {
            hls.destroy();
        };
    }, [url, hlsSessionNonce]);
    useEffect(() => {
        if (!debug) {
            return;
        }
        const video = videoRef.current;
        if (!video) {
            return;
        }
        // Polls current playback metrics for the debug panel.
        const timer = window.setInterval(() => {
            setCurrentTimeSeconds(video.currentTime || 0);
            setDurationSeconds(Number.isFinite(video.duration) ? video.duration : 0);
            setBufferedAheadSeconds(getBufferedAheadSeconds(video));
            setBufferedRangesSummary(getBufferedRangesSummary(video));
            setVideoReadyState(video.readyState);
            setVideoNetworkState(video.networkState);
            setPaused(video.paused);
        }, 500);
        return () => {
            window.clearInterval(timer);
        };
    }, [url]);
    return (_jsxs("div", { style: { width: '100%' }, children: [_jsx("video", { ref: videoRef, src: hlsSupported ? undefined : url, controls: true, autoPlay: true, playsInline: true, preload: 'metadata', style: { width: '100%' }, onCanPlay: onCanPlay, onCanPlayThrough: onCanPlayThrough, onTimeUpdate: onTimeUpdate, onError: onError, onAbort: onAbort, onStalled: onStalled, onSuspend: onSuspend, onWaiting: onWaiting, onEmptied: onEmptied }), unplayableSegment.detected ? (_jsx("div", { style: {
                    marginBottom: 8,
                    padding: '8px 10px',
                    borderRadius: 4,
                    background: '#fdecea',
                    color: '#611a15',
                    border: '1px solid #f5c2c0',
                    fontSize: 13,
                }, children: "Sorry this fragment could not be played." })) : null, debug ? (_jsxs("div", { style: { marginBottom: 8, fontSize: 12, lineHeight: 1.4 }, children: [_jsx("div", { children: "debug (development): on" }), _jsxs("div", { children: ["url: ", url || 'none'] }), _jsxs("div", { children: ["hls.js supported: ", hlsSupported ? 'yes' : 'no'] }), _jsxs("div", { children: ["hls status: ", hlsSupported ? hlsStatus : 'native playback (hls.js unsupported)'] }), _jsxs("div", { children: ["last event: ", playerEvent] }), _jsxs("div", { children: ["video: ", paused ? 'paused' : 'playing'] }), _jsxs("div", { children: ["time: ", currentTimeSeconds.toFixed(2), " / ", durationSeconds.toFixed(2), " sec"] }), _jsxs("div", { children: ["buffer ahead: ", bufferedAheadSeconds.toFixed(2), " sec"] }), _jsxs("div", { children: ["buffered ranges: ", bufferedRangesSummary] }), _jsxs("div", { children: ["ready/network state: ", videoReadyState, "/", videoNetworkState] }), _jsxs("div", { children: ["last error: ", lastHlsError] }), _jsxs("div", { children: ["last recovery action: ", lastRecoveryAction] }), _jsxs("div", { children: ["segment unplayable: ", unplayableSegment.detected ? 'yes' : 'no'] }), unplayableSegment.detected ? (_jsxs("div", { children: ["unplayable window: sn ", unplayableSegment.firstFragSn, " to ", unplayableSegment.lastFragSn, ", t=", unplayableSegment.fromSec.toFixed(3), "-", unplayableSegment.toSec.toFixed(3), " sec (", unplayableSegment.reason, ")"] })) : null, unplayableSegment.detected ? (_jsxs("div", { children: ["context before issue: sn ", unplayableSegment.beforeFragSn, ", end=", unplayableSegment.beforeFragEndSec.toFixed(3), " sec"] })) : null, unplayableSegment.detected ? _jsxs("div", { children: ["fragment timeline around issue: ", unplayableSegment.timelineSummary] }) : null, unplayableSegment.detected ? (_jsxs(_Fragment, { children: [_jsxs("div", { children: ["incident report: ", unplayableIncidentReport] }), _jsx("button", { type: 'button', onClick: () => {
                                    void navigator.clipboard.writeText(unplayableIncidentReport).catch((err) => {
                                        console.error('failed to copy incident report', err);
                                    });
                                }, style: { marginTop: 4 }, children: "Copy Incident Report" })] })) : null, _jsxs("div", { children: ["error counts: ", Object.keys(errorCountByKey).length > 0 ? JSON.stringify(errorCountByKey) : 'none'] }), _jsxs("div", { children: ["buffer lifecycle:", bufferLifecycleEvents.length > 0
                                ? bufferLifecycleEvents.map((eventLine, index) => (_jsxs("div", { children: [index + 1, ". ", eventLine] }, `${eventLine}-${index}`)))
                                : ' none'] }), _jsxs("div", { children: ["recent hls errors:", recentErrorSnapshots.length > 0
                                ? recentErrorSnapshots.map((snapshot, index) => (_jsxs("div", { children: [index + 1, ". ", snapshot.at, " ", snapshot.fatal ? 'fatal' : 'nonfatal', " ", snapshot.type, ":", snapshot.details, " evt=", snapshot.event, ' ', "t=", snapshot.currentTime.toFixed(2), "/", snapshot.duration.toFixed(2), " ahead=", snapshot.bufferedAhead.toFixed(2), "buffered=[", snapshot.bufferedRanges, "] rs/ns=", snapshot.readyState, "/", snapshot.networkState, " ", snapshot.paused ? 'paused' : 'playing', " action=", snapshot.recoveryAction, ' ', "parent=", snapshot.parent, " attached=", snapshot.mediaAttached ? 'yes' : 'no', " reason=", snapshot.reason, " srcErr=", snapshot.sourceErrorName, ":", snapshot.sourceErrorCode, " msg=", snapshot.sourceErrorMessage, ' ', "frag(sn=", snapshot.fragSn, ",cc=", snapshot.fragCc, ",start=", snapshot.fragStart, ",dur=", snapshot.fragDuration, ",lvl=", snapshot.level, ",part=", snapshot.partIndex, ")", ' ', "rel=", snapshot.fragRelUrl] }, `${snapshot.at}-${index}`)))
                                : ' none'] })] })) : null] }));
};
